const FETCH_CONCURRENCY = 25
const INLINE_AVAILABILITY_SELECTOR = "#nosto-collection-availability"

/**
 * Per-product availability lookup keyed by Nosto's `productId` (the numeric
 * Shopify product ID, locale-independent).
 *
 * Primary source: an inline `<script id="nosto-collection-availability">` map
 * rendered by `nosto-collection.liquid` from `collection.products`. Keys are
 * `product.id` (stable) rather than `product.handle` (locale-translated), so
 * the same map covers every locale of a given market — Liquid emits French
 * handles on `/fr-ca/` and English handles on `/en-ca/` for the same product,
 * but Nosto always returns the primary-locale handle plus `productId`. Joining
 * on `productId` is the only way to keep en-ca and fr-ca filtering identical.
 *
 * Fallback source: per-product `/products/{handle}.js` for products not in the
 * inline map (collections > 50, or pages where the inline map is missing).
 * On the base market (no URL locale slug, `routes.root === "/"`) the path stays
 * bare. On localized markets it is anchored to `/en-${country}/...` so both
 * en-ca and fr-ca hit the same route — `/fr-ca/products/X.js` returns 404 for
 * products without French translation even when they are buyable in CA.
 * Anchoring to the market's primary English locale keeps the fallback responses
 * locale-independent.
 *
 * Returns a Map keyed by Nosto `productId`. Unknown products default to `true`
 * so a coverage gap never shrinks the catalog. An explicit 404 from the
 * fallback is treated as unavailable.
 */
export type AvailabilityLookup = {
  productId?: string | number
  handle?: string
}

export async function fetchAvailability(
  hits: readonly AvailabilityLookup[]
): Promise<Map<string, boolean>> {
  if (hits.length === 0) {
    return new Map()
  }

  const result = new Map<string, boolean>()
  const inline = readInlineAvailability()
  const missing: AvailabilityLookup[] = []

  for (const hit of hits) {
    const id = normalizeId(hit.productId)
    if (id != null && id in inline) {
      result.set(id, inline[id])
    } else {
      missing.push(hit)
    }
  }

  if (missing.length === 0) {
    return result
  }

  for (let i = 0; i < missing.length; i += FETCH_CONCURRENCY) {
    const batch = missing.slice(i, i + FETCH_CONCURRENCY)
    const entries = await Promise.all(batch.map(hit => fetchHit(hit)))
    for (const [id, available] of entries) {
      if (id != null) {
        result.set(id, available)
      }
    }
  }
  return result
}

function normalizeId(value: unknown): string | null {
  if (typeof value === "string" && value.length > 0) return value
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return null
}

function readInlineAvailability(): Record<string, boolean> {
  if (typeof document === "undefined") return {}
  const script = document.querySelector(INLINE_AVAILABILITY_SELECTOR)
  const body = script?.textContent?.trim()
  if (!body) return {}
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>
    const map: Record<string, boolean> = {}
    for (const [id, available] of Object.entries(parsed)) {
      if (typeof id === "string" && id.length > 0) {
        map[id] = Boolean(available)
      }
    }
    return map
  } catch (error) {
    console.error("nosto-collection-availability JSON parse failed", error)
    return {}
  }
}

async function fetchHit(hit: AvailabilityLookup): Promise<[string | null, boolean]> {
  const id = normalizeId(hit.productId)
  if (typeof hit.handle !== "string" || hit.handle.length === 0) {
    return [id, true]
  }
  try {
    const response = await fetch(`${stableLocalePrefix()}products/${encodeURIComponent(hit.handle)}.js`, {
      headers: { Accept: "application/json" }
    })
    if (response.status === 404) {
      return [id, false]
    }
    if (!response.ok) {
      return [id, true]
    }
    const data = (await response.json()) as { available?: boolean }
    return [id, Boolean(data.available)]
  } catch (error) {
    console.error(`nosto availability fetch failed for handle "${hit.handle}"`, error)
    return [id, true]
  }
}

type ShopifyWindow = Window & {
  Shopify?: { country?: string; routes?: { root?: string } }
}

function stableLocalePrefix(): string {
  if (typeof window === "undefined") return "/"
  const shopify = (window as ShopifyWindow).Shopify
  const root = shopify?.routes?.root

  // Base market carries no URL locale slug — Shopify reports routes.root as "/".
  // Detect it FIRST so we never synthesize an /en-us/ prefix that 404s once the
  // US slug is removed. Forward-compatible: while en-us still exists, root is
  // "/en-us/" and the country branch below still yields it.
  if (root === "/") return "/"

  // Localized markets (Canada): anchor to the market's primary English locale so
  // en-ca and fr-ca share one route — Nosto returns English handles and
  // /fr-ca/products/{en-handle}.js 404s when no French handle exists.
  const country = shopify?.country
  if (typeof country === "string" && /^[A-Z]{2}$/.test(country)) {
    return `/en-${country.toLowerCase()}/`
  }

  if (typeof root !== "string" || root.length === 0) return "/"
  return root.endsWith("/") ? root : `${root}/`
}
